import { __assign, __extends } from "tslib";
import * as React from 'react';
import { NavLink } from './NavLink';
import { classNamesFunction, FocusZone, KeyCodes } from 'office-ui-fabric-react';
var getClassNames = classNamesFunction();
var idIncrementer = 0;
var NavLinkGroupBase = /** @class */ (function (_super) {
    __extends(NavLinkGroupBase, _super);
    function NavLinkGroupBase(props) {
        var _this = _super.call(this, props) || this;
        _this.state = {
            isExpanded: !!(props.isExpanded !== undefined ? props.isExpanded : props.defaultIsExpanded),
            isKeyboardExpanded: false
        };
        _this.navLinkGroupRef = React.createRef();
        _this.navRootRef = React.createRef();
        _this._onLinkClicked = _this._onLinkClicked.bind(_this);
        _this._offsetUpdated = _this._offsetUpdated.bind(_this);
        _this._nestedNavBlur = _this._nestedNavBlur.bind(_this);
        _this._escapeSubNavFocus = _this._escapeSubNavFocus.bind(_this);
        _this.instanceNumber = idIncrementer++;
        return _this;
    }
    NavLinkGroupBase.prototype.render = function () {
        var _a = this.props, link = _a.link, isNavCollapsed = _a.isNavCollapsed, hasSelectedNestedLink = _a.hasSelectedNestedLink, styles = _a.styles, theme = _a.theme, itemTotal = _a.itemTotal, itemStartIndex = _a.itemStartIndex;
        var isKeyboardExpanded = this.state.isKeyboardExpanded;
        var isExpanded = this.props.isExpanded === undefined ? this.state.isExpanded : this.props.isExpanded;
        var classNames = getClassNames(styles, {
            isExpanded: isExpanded,
            isNavCollapsed: isNavCollapsed,
            isKeyboardExpanded: isKeyboardExpanded,
            theme: theme
        });
        var NestedComponent = isNavCollapsed && isKeyboardExpanded ? FocusZone : 'ul';
        // ensuring our id's don't have spaces in them so we don't fail accessibility insights tests.
        var menuId = link.name.split(' ').join('') + 'menu_id' + this.instanceNumber;
        var linkId = link.name.split(' ').join('') + '_id' + this.instanceNumber;
        return (React.createElement("div", __assign({ className: classNames.root, role: "presentation" }, (isNavCollapsed && link.links && { onMouseEnter: this._offsetUpdated, ref: this.navRootRef })),
            React.createElement(NavLink, __assign({ item: link, role: "menuitem", primaryIconName: link.icon }, link, { isNavCollapsed: isNavCollapsed, onClick: this._onLinkClicked, "aria-controls": menuId, "aria-expanded": isExpanded }, (isNavCollapsed && link.links && { 'aria-haspopup': true, 'aria-expanded': isKeyboardExpanded }), { isSelected: hasSelectedNestedLink, hasSelectedNestedLink: hasSelectedNestedLink, hasNestedMenu: true, isExpanded: isExpanded, id: linkId, "aria-setsize": itemTotal, "aria-posinset": itemStartIndex })),
            link.links && (React.createElement("div", __assign({ className: classNames.nestedNav, role: "presentation" }, (isNavCollapsed && { ref: this.navLinkGroupRef, 'data-is-focusable': false })),
                isNavCollapsed && (React.createElement(NavLink, { isNavCollapsed: isNavCollapsed, name: link.name, "data-is-focusable": false, "aria-hidden": true, primaryIconName: link.icon, disabled: true, styles: {
                        root: classNames.nestedNavHeaderItem,
                        text: classNames.nestedNavHeaderItemText,
                        icon: classNames.nestedNavHeaderItemIcon
                    } })),
                React.createElement("div", { className: classNames.nestedNavLinksWrapper, role: "presentation" },
                    React.createElement(NestedComponent, __assign({ className: classNames.nestedNavLinks, "aria-labelledby": linkId, role: "menu", id: menuId }, (isKeyboardExpanded &&
                        isNavCollapsed && {
                        componentRef: this._keyboardFocusSubNav,
                        onKeyDown: this._escapeSubNavFocus,
                        isCircularNavigation: true,
                        as: 'ul',
                        onBlur: this._nestedNavBlur
                    })), link.links.map(function (nestedLink, index) {
                        return (React.createElement("li", { role: "presentation", key: nestedLink.key },
                            React.createElement(NavLink, __assign({ item: nestedLink, "aria-setsize": link.links.length, "aria-posinset": index + 1, role: "menuitem", primaryIconName: nestedLink.icon }, nestedLink, { isNavCollapsed: isNavCollapsed }, (nestedLink.isSelected && { 'aria-current': 'page' }), { hasNestedMenu: false, hasSelectedNestedLink: false, isNested: true, isParentExpanded: isExpanded }))));
                    })))))));
    };
    NavLinkGroupBase.prototype._onLinkClicked = function (ev, item) {
        var _this = this;
        this.setState(__assign(__assign({}, (this.props.isNavCollapsed && { isKeyboardExpanded: !this.state.isKeyboardExpanded })), (this.props.isExpanded === undefined && {
            isExpanded: !this.state.isExpanded
        })), function () {
            if (_this.props.onCollapse) {
                _this.props.onCollapse();
            }
        });
        if (this.props.isNavCollapsed) {
            this._offsetUpdated();
        }
        if (this.props.onExpanded) {
            this.props.onExpanded(!this.state.isExpanded);
        }
        if (this.props.link.onClick) {
            this.props.link.onClick(ev, item);
        }
    };
    // We're using the ref callback to focus the element so we can guarantee the element exists
    NavLinkGroupBase.prototype._keyboardFocusSubNav = function (focusZone) {
        if (focusZone) {
            focusZone.focus(true);
        }
    };
    NavLinkGroupBase.prototype._escapeSubNavFocus = function (event) {
        if (event.which === KeyCodes.escape) {
            this.setState({
                isKeyboardExpanded: false
            });
            if (this.props.focusZoneRef.current) {
                this.props.focusZoneRef.current.focus();
            }
        }
    };
    NavLinkGroupBase.prototype._nestedNavBlur = function (event) {
        var relatedTarget = event.relatedTarget;
        if (event.relatedTarget === null) {
            // In IE11, due to lack of support, event.relatedTarget is always
            // null making every onBlur call to be "outside" of the Nav
            // even when it's not. Using document.activeElement is another way
            // for us to be able to get what the relatedTarget without relying
            // on the event
            relatedTarget = document.activeElement;
        }
        if (!event.currentTarget.contains(relatedTarget)) {
            this.setState({
                isKeyboardExpanded: false
            });
        }
    };
    // calculate the offset due to scroll so we always position the sub nav correctly
    NavLinkGroupBase.prototype._offsetUpdated = function (_ev) {
        if (this.navRootRef.current && this.navLinkGroupRef.current && this.props.navRef.current) {
            this.navLinkGroupRef.current.style.top =
                this.navRootRef.current.offsetTop - this.props.navRef.current.scrollTop + 'px';
        }
    };
    return NavLinkGroupBase;
}(React.Component));
export { NavLinkGroupBase };
//# sourceMappingURL=NavLinkGroupBase.js.map